import { differenceInMinutes, fromUnixTime } from 'date-fns';
import { decodeJwt } from 'jose';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useInterval } from 'usehooks-ts';

import { Auth0ContextInterface, Auth0Provider, useAuth0, User } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';

import { setAuth0User, setBearerToken } from '../store/slices/authSlice';
import { useAppDispatch } from '../store/store';
import { authorizationParams } from 'src/services/auth0/config';

const Auth0Context = createContext<Auth0ContextInterface<User> | undefined>(undefined);

export function Auth0Wrapper({ children }: { children: ReactNode }) {
    return (
        <Auth0Provider
            domain={import.meta.env.VITE_AUTH0_DOMAIN}
            clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
            authorizationParams={authorizationParams}
            cacheLocation="localstorage" // Use local storage to persist tokens across sessions
            useRefreshTokens
            // See: https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/18
            useRefreshTokensFallback
        >
            <Auth0ContextProvider>{children}</Auth0ContextProvider>
        </Auth0Provider>
    );
}

const REFRESH_INTERVAL = 5_000; // 5 seconds in milliseconds

const ExcludedFromLogout = ['/login', '/join', '/auth-confirmed'];

function Auth0ContextProvider({ children }: { children: ReactNode }) {
    const auth0 = useAuth0();
    const { isAuthenticated, isLoading, getAccessTokenSilently, user, logout } = auth0;

    const dispatch = useAppDispatch();

    // Handle absolute token expiration (when refresh token is also expired)
    const handleSessionExpired = () => {
        if (ExcludedFromLogout.includes(window.location.pathname)) {
            return;
        }

        logout({
            logoutParams: {
                returnTo: `${window.location.origin}/login`,
            },
        });
    };

    useInterval(
        () => {
            if (!isAuthenticated || isLoading) {
                return;
            }

            // Refresh token logic - handles absolute expiration
            const refreshToken = async () => {
                try {
                    await getAccessTokenSilently({ cacheMode: 'off' });
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    datadogLogs.logger.error('Token refresh failed', undefined, error);

                    // Check if this is a "login_required" error, which means refresh token is expired
                    const errorObj = error as { error?: string; message?: string };
                    if (
                        errorObj?.error === 'login_required' ||
                        errorObj?.message?.includes('login_required')
                    ) {
                        handleSessionExpired();
                    }
                }
            };

            // Check token expiration
            const checkTokenExpiration = async () => {
                try {
                    const token = await getAccessTokenSilently({ cacheMode: 'cache-only' });
                    if (!token) {
                        console.warn('No token available, attempting refresh');
                        await refreshToken();
                        return;
                    }

                    const payload = decodeJwt(token);
                    if (!payload.exp) {
                        console.warn('Token missing expiration claim, refreshing as precaution');
                        await refreshToken();
                        return;
                    }

                    const expirationDate = fromUnixTime(payload.exp);
                    const minutesUntilExpiration = differenceInMinutes(expirationDate, new Date());

                    // If token is already expired, try to refresh
                    if (minutesUntilExpiration < 0) {
                        await refreshToken();
                        return;
                    }

                    // If token expires soon (within 5 minutes), proactively refresh
                    if (minutesUntilExpiration < 5) {
                        await refreshToken();
                        return;
                    }
                } catch (error) {
                    datadogLogs.logger.error(
                        'Error checking token expiration',
                        undefined,
                        error as Error
                    );
                    // If we can't check expiration, try to refresh anyway
                    await refreshToken();
                }
            };

            checkTokenExpiration();
        },
        isAuthenticated && !isLoading ? REFRESH_INTERVAL : null
    );

    useEffect(() => {
        if (isAuthenticated) {
            getAccessTokenSilently().then((token) => {
                dispatch(setBearerToken(token));
            });
        }
    }, [isAuthenticated, getAccessTokenSilently, dispatch]);

    useEffect(() => {
        if (user) {
            dispatch(setAuth0User(user));
        }
    }, [user, dispatch]);

    return <Auth0Context.Provider value={auth0}>{children}</Auth0Context.Provider>;
}

export function useAuth0Context() {
    const context = useContext(Auth0Context);
    if (context === undefined) {
        throw new Error('useAuth0Context must be used within Auth0Wrapper');
    }
    return context;
}
